import { useState, useEffect } from 'react'
import type { NextPage } from 'next'
import {
  AppLayout,
  SelectInput,
  CurrencyInput,
  ButtonGroup,
  DateInput,
  ImageKit,
  TextAreaInput,
  Button,
  Tooltip,
  TextInput,
  DocumentUploadPopup,
  Container,
} from '@components'
import { formatCurrency } from '@utils'
import { useRouter } from 'next/router'
import { InvestmentInvestorDetailsSectionLoader } from '@loaders'
import axios from 'axios'
import useRequireLogin from 'hooks/useRequireLogin'
import { NextSeo } from 'next-seo'

interface IFormData {
  companyName: string
  howAcquireShares: any
  numberOfShares: number
  sharePrice: number
  isVestedShares: boolean
  isAnyRestrictionsToSellYourShares: boolean
  lastFundingRoundSharePrice: number
  deadlineAt: Date | null
  shareType: string
  notes: string
  agreement: boolean
}
interface IDocumentFormValues {
  name: string
  uploadedFile: any
  documentType: string
}

const InvestmentDocuments = [
  {
    text: 'Select',
    value: '',
  },
  {
    text: 'Private Placement Memorandum (PPM)',
    value: 'Private Placement Memorandum (PPM)',
  },
  {
    text: 'Operating Agreement',
    value: 'Operating Agreement',
  },
  {
    text: 'Subscription Agreement',
    value: 'Subscription Agreement',
  },
  {
    text: 'Contract Note',
    value: 'Contract Note',
  },
  {
    text: 'Side Letter',
    value: 'Side Letter',
  },
  {
    text: '2021 Final Statement',
    value: '2021 Final Statement',
  },
  {
    text: '2022 Interim Statement',
    value: '2022 Interim Statement',
  },
  {
    text: '2022 Final Statement',
    value: '2022 Final Statement',
  },
  {
    text: 'Loan agreement',
    value: 'Loan agreement',
  },
  {
    text: 'Share certificate',
    value: 'Share certificate',
  },
  {
    text: 'Other',
    value: 'Other',
  },
]

const TaxDocuments = [
  {
    text: 'Select',
    value: '',
  },
  {
    text: '2019 K1',
    value: '2019 K1',
  },
  {
    text: '2020 K1',
    value: '2020 K1',
  },
  {
    text: '2021 K1',
    value: '2021 K1',
  },
  {
    text: '2022 K1',
    value: '2022 K1',
  },
]

const typeOfShares = [
  {
    name: 'Select from the list of shares type',
    value: '',
  },
  {
    name: 'Common',
    value: 'Common',
  },
  {
    name: 'Preferred Series A',
    value: 'Preferred Series A',
  },
  {
    name: 'Preferred Series B',
    value: 'Preferred Series B',
  },
  {
    name: 'Preferred Series C',
    value: 'Preferred Series C',
  },
  {
    name: 'Preferred Series D',
    value: 'Preferred Series D',
  },
  {
    name: 'Preferred Series E',
    value: 'Preferred Series E',
  },
  {
    name: 'Preferred Series F+',
    value: 'Preferred Series F+',
  },
  {
    name: 'Preferred Series Unknown',
    value: 'Preferred Series Unknown',
  },
  {
    name: 'Convertibles',
    value: 'Convertibles',
  },
  {
    name: 'Other',
    value: 'Other',
  },
]

const AcquiredShareOption = [
  {
    label: 'Direct',
    value: 'Direct',
  },
  {
    label: 'Stock Options',
    value: 'Stock Options',
  },
  {
    label: 'Fund',
    value: 'Fund',
  },
  {
    label: 'Other',
    value: 'Other',
  },
]

const ListYourShares: NextPage = () => {
  useRequireLogin()
  const router = useRouter()
  const [loading, setLoading] = useState<boolean>(true)
  const [companies, setCompanies] = useState([])
  const [otherCompanyName, setOtherCompanyName] = useState<string>('')
  const [documentUploadPopup, setDocumentUploadPopup] = useState<boolean>(false)
  const [formData, setFormData] = useState<IFormData>({
    companyName: '',
    howAcquireShares: [],
    numberOfShares: 0,
    sharePrice: 0,
    isVestedShares: true,
    isAnyRestrictionsToSellYourShares: false,
    lastFundingRoundSharePrice: 0,
    deadlineAt: null,
    shareType: '',
    notes: '',
    agreement: false,
  })
  const [documentFormValues, setDocumentFormValues] =
    useState<IDocumentFormValues>({
      uploadedFile: null,
      name: '',
      documentType: '',
    })
  const [uploadedDocuments, setUploadedDocuments] = useState<any[]>([])
  const [submitting, setSubmitting] = useState<boolean>(false)

  const setDocumentValue = (value: any, name: keyof IDocumentFormValues) => {
    if (name === 'documentType' && !value) {
      setDocumentFormValues({
        ...documentFormValues,
        [name]: value,
        name: '',
      })
    } else {
      setDocumentFormValues({ ...documentFormValues, [name]: value })
    }
  }

  const handleChange = (value: any, key: string) => {
    setFormData(d => ({ ...d, [key]: value }))
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const { companyName, howAcquireShares } = formData

    const _companyName =
      companyName === 'other' ? otherCompanyName : companyName

    const _company = companies.find(
      (company: any) => company.value === companyName,
    )

    const _comma_separated_acquire_your_shares = howAcquireShares.map(
      (acquireYourShare: any) => acquireYourShare.value,
    )

    const _values = {
      ...formData,
      companyName: _companyName,
      company: _company ? _company['companyId'] : null,
      howAcquireShares: String(_comma_separated_acquire_your_shares),
    }
    setSubmitting(true)
    axios
      .post(process.env.NEXT_PUBLIC_API_URL + '/me/shareListings', _values)
      .then(response => {
        axios
          .post(
            process.env.NEXT_PUBLIC_API_URL +
              `/me/shareListing/documents/${response?.data?.id}`,
            uploadedDocuments,
          )
          .then(response => {
            router.push('/list-your-shares/confirmation')
          })
          .catch(error => {
            setSubmitting(false)
          })
      })
      .catch(error => {
        console.log(error)
        setSubmitting(false)
      })
  }

  const submitDocumentForm = async (e: React.SyntheticEvent) => {
    e.preventDefault()
    let documentFormData = new FormData()
    documentFormData.append('directory', 'shareListings')
    documentFormData.append('files', documentFormValues.uploadedFile)

    setSubmitting(true)

    axios
      .post(
        process.env.NEXT_PUBLIC_API_URL + '/media/upload',
        documentFormData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      .then(res => {
        setUploadedDocuments([
          ...uploadedDocuments,
          {
            name: documentFormValues.name,
            documentType: documentFormValues.documentType,
            url: res?.data?.filePath,
          },
        ])
        setSubmitting(false)
        setDocumentUploadPopup(false)
        setDocumentFormValues({
          uploadedFile: null,
          name: '',
          documentType: '',
        })
      })
      .catch(error => {
        setSubmitting(false)
        setDocumentUploadPopup(false)
      })
  }

  const closeDocumentUploadPopup = () => {
    setDocumentFormValues({
      uploadedFile: null,
      name: '',
      documentType: '',
    })
    setDocumentUploadPopup(false)
  }

  useEffect(() => {
    axios
      .get(process.env.NEXT_PUBLIC_API_URL + '/companies')
      .then(response => {
        const _companies = response.data.map((c: any) => ({
          value: c.name,
          text: c.name,
          companyId: c.id,
        }))
        _companies.unshift({
          value: '',
          text: 'Select',
        })
        _companies.push({
          value: 'other',
          text: 'Other',
        })

        setCompanies(_companies)
        setLoading(false)
      })
      .catch(error => {
        console.log(error)
        setLoading(false)
      })
  }, [])

  return (
    <AppLayout>
      <NextSeo
        title={'List Your Shares'}
        description="Access liquidity by listing your shares on MarketX Ventures."
        canonical={'https://www.marketxventures.com/list-your-shares'}
        openGraph={{
          title: 'List Your Shares | MarketX Ventures',
          url: 'https://www.marketxventures.com/list-your-shares',
        }}
      />
      <Container>
        {documentUploadPopup ? (
          <DocumentUploadPopup
            submitDocumentForm={submitDocumentForm}
            setDocumentValue={setDocumentValue}
            documentFormValues={documentFormValues}
            submitting={submitting}
            closeDocumentUploadPopup={closeDocumentUploadPopup}
            InvestmentDocuments={InvestmentDocuments}
            TaxDocuments={TaxDocuments}
          />
        ) : (
          <div className="mt-12 md:mt-0">
            <div>
              <div className="my-6 text-center text-lg font-semibold text-themeBlack md:text-xl">
                <h2>Sell My Shares</h2>
              </div>
              <p className="mx-auto my-6 w-full text-center text-themeGray-dark2 md:w-3/5">
                Tell us about your company shares information. We will review
                them and if they meet our guidelines, we can help you find
                potential buyers who can find your offering on our platform,
                where they can invest, sign documents and finalize the
                transaction all on MarketX.
              </p>
            </div>

            <div>
              {loading ? (
                <div className="flex justify-center">
                  <InvestmentInvestorDetailsSectionLoader />
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="mx-auto w-full rounded-md border border-themeGray px-4 py-8 md:w-2/3 lg:px-10">
                    <div className="grid grid-cols-1 items-end gap-x-8 gap-y-4 md:grid-cols-2">
                      <div className="mb-0 lg:mb-6">
                        <SelectInput
                          required
                          tooltip="Name of the company where you own shares that you like to sell"
                          options={companies}
                          name="companyName"
                          label="Company name"
                          defaultValue={formData.companyName}
                          onChange={(e: any) => {
                            handleChange(e.target.value, 'companyName')
                            setOtherCompanyName('')
                          }}
                        />
                      </div>

                      <div>
                        {formData.companyName === 'other' && (
                          <div className="mb-6">
                            <TextInput
                              required
                              label="Company name (other)"
                              name="otherCompanyName"
                              onChange={(e: any) => {
                                setOtherCompanyName(e.target.value)
                              }}
                            />
                          </div>
                        )}
                      </div>

                      <div className="mb-6">
                        <SelectInput
                          multiple
                          name="howAcquireShares"
                          label="How did you acquire your shares?"
                          options={AcquiredShareOption}
                          onChange={(value: any) => {
                            handleChange(value, 'howAcquireShares')
                          }}
                        />
                      </div>
                      <div className="mb-6">
                        <ButtonGroup
                          label="Are these shares vested?"
                          value={formData?.isVestedShares}
                          onChange={(value: string) => {
                            handleChange(value, 'isVestedShares')
                          }}
                          options={[
                            {
                              label: 'Yes',
                              value: true,
                            },
                            {
                              label: 'No',
                              value: false,
                            },
                          ]}
                        />
                      </div>

                      <div className="mb-6">
                        <CurrencyInput
                          required
                          tooltip="What is the desired price per share that you would like to receive?"
                          name="sharePrice"
                          label="Asking price per share (US$)"
                          value={formData.sharePrice}
                          onChange={(value: any) => {
                            handleChange(parseInt(value), 'sharePrice')
                          }}
                        />
                      </div>
                      <div className="mb-6">
                        <div className="flex items-center">
                          <label
                            htmlFor="numberOfShares"
                            className="block text-base font-medium text-gray-700">
                            Number of (vested) shares
                          </label>
                          <Tooltip
                            message="How many shares that are vested which you like to sell?"
                            position="top">
                            <div className="mb-0 ml-1 flex cursor-pointer items-center">
                              <ImageKit
                                src="/images/question.svg"
                                alt="Tooltip image"
                                height={16}
                                width={16}
                              />
                            </div>
                          </Tooltip>
                        </div>
                        <div className="mt-1 flex">
                          <input
                            required
                            type="number"
                            name="numberOfShares"
                            value={formData.numberOfShares}
                            className="block w-2/4 rounded border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                            onChange={(e: any) => {
                              handleChange(e.target.value, 'numberOfShares')
                            }}
                          />
                          <input
                            disabled
                            className="-ml-1 w-2/4 rounded-br rounded-tr bg-primary text-white"
                            type="text"
                            value={`= ${
                              formData.sharePrice && formData.numberOfShares
                                ? formatCurrency(
                                    formData.numberOfShares *
                                      formData.sharePrice,
                                  )
                                : 0
                            }`}
                          />
                        </div>
                      </div>

                      <div className="mb-6">
                        <ButtonGroup
                          label="Are there any restrictions to sell your shares?"
                          value={formData?.isAnyRestrictionsToSellYourShares}
                          onChange={(value: string) => {
                            handleChange(
                              value,
                              'isAnyRestrictionsToSellYourShares',
                            )
                          }}
                          options={[
                            {
                              label: 'Yes',
                              value: true,
                            },
                            {
                              label: 'No',
                              value: false,
                            },
                          ]}
                        />
                      </div>
                      <div className="mb-6">
                        <CurrencyInput
                          tooltip="Please tell us the approx. share price associated with the company's last funding round, if known"
                          name="lastFundingRoundSharePrice"
                          label="Last funding round share price (if known)"
                          value={formData.lastFundingRoundSharePrice}
                          onChange={(value: any) => {
                            handleChange(
                              parseInt(value),
                              'lastFundingRoundSharePrice',
                            )
                          }}
                        />
                      </div>
                      <div className="mb-6">
                        <DateInput
                          isPastDate
                          label="Deadline to exercise my stock options (estimate if unknown)"
                          value={formData?.deadlineAt}
                          onChange={(value: Date) => {
                            handleChange(value, 'deadlineAt')
                          }}
                        />
                      </div>
                      <div className="mb-6">
                        <SelectInput
                          options={typeOfShares?.map(typeOfShare => ({
                            value: typeOfShare.value,
                            text: typeOfShare.name,
                          }))}
                          name="shareType"
                          label="Type of shares available to sell"
                          onChange={(e: any) => {
                            handleChange(e.target.value, 'shareType')
                          }}
                        />
                      </div>
                    </div>
                    <div className="mb-6">
                      <div className="mb-1 flex items-center">
                        <p className="block text-left text-base font-medium text-themeBlack">
                          Upload support documents
                        </p>
                        <Tooltip
                          message="Please upload the supporting documents for the private company shares you own, including the Grant Document, Stock Option Plan, Shareholding Agreement, etc."
                          position="top">
                          <div className="mb-0 ml-1 flex cursor-pointer items-center">
                            <ImageKit
                              src="/images/question.svg"
                              alt="Tooltip image"
                              height={16}
                              width={16}
                            />
                          </div>
                        </Tooltip>
                      </div>
                      <label
                        className="flex w-full cursor-pointer justify-between rounded border border-gray-300 p-2 text-left text-sm"
                        onClick={() => setDocumentUploadPopup(true)}>
                        Select Document
                        <ImageKit
                          src="/images/file_upload_icon.svg"
                          alt="file upload icon"
                          width={18}
                          height={18}
                        />
                      </label>

                      {uploadedDocuments.length > 0 && (
                        <div className="relative my-4">
                          <div className="flex items-center">
                            <ImageKit
                              alt="Company Name"
                              src="/images/doc-detail.svg"
                              width={24}
                              height={24}
                            />
                            <p className="ml-4 text-sm font-semibold text-[#222222]">
                              Documents
                            </p>
                          </div>
                          {uploadedDocuments.map((document, index) => (
                            <div className="relative flex" key={index}>
                              <div className="ml-3 flex border-l-[1.5px] border-primary pl-[26px] before:absolute before:bottom-0 before:left-[12px] before:h-[1px] before:w-2 before:bg-primary">
                                <a
                                  href={document?.url}
                                  target="_blank"
                                  rel="noopener noreferrer">
                                  {' '}
                                  <p className="translate-y-2.5 pr-2 text-sm text-primary">
                                    {document?.name}
                                  </p>
                                </a>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="mb-6">
                      <TextAreaInput
                        name="notes "
                        label="Tell us about your shares, including possible restrictions and vesting terms."
                        placeholder="Type..."
                        value={formData.notes}
                        areaHeight={3}
                        onChange={(e: any) => {
                          handleChange(e.target.value, 'notes')
                        }}
                      />
                    </div>
                    <div className="mb-6">
                      <div className="flex h-5 items-center">
                        <input
                          checked={formData.agreement}
                          required
                          onChange={e =>
                            handleChange(e.target.checked, 'agreement')
                          }
                          name="agreement"
                          id="agreement"
                          type="checkbox"
                          className={
                            'h-4 w-4 rounded border-gray-300 text-primary focus:ring-0'
                          }
                        />
                        <div className="ml-3 mt-4 text-sm">
                          <label
                            htmlFor="agreement"
                            className="cursor-pointer font-medium text-gray-700">
                            By submitting this request, you agree to MarketX
                            Ventures{' '}
                            <a
                              rel="noreferrer"
                              className="text-link"
                              href="https://docs.google.com/document/d/e/2PACX-1vRCqfJQoKGhi7SP9xk9H5gh0ypxp0RHRC4PbYn-aLYK8x6NdkMh_CNcXdUs86pHuwkH2M548ejrgtVd/pub"
                              target="_blank">
                              Terms & Conditions
                            </a>
                            ,{' '}
                            <a
                              rel="noreferrer"
                              className="text-link"
                              href="https://docs.google.com/document/u/1/d/e/2PACX-1vRn7Fe07NFLLy6eo84S0Y7N0eBbgg3n8DPnzFaRlDfwuHmZriSSj0kThBEiEz8UWL_7yMvlPH248TMf/pub"
                              target="_blank">
                              Privacy Policy
                            </a>{' '}
                            and the terms of Seller Engagement Letter and NDA
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="my-12 flex items-center justify-center">
                    <Button secondary className="mr-2" disabled={submitting}>
                      Back
                    </Button>
                    <Button submit disabled={submitting} loading={submitting}>
                      Submit
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </Container>
    </AppLayout>
  )
}

export default ListYourShares
